// DataTable component for displaying bus data with pagination from API

import { useState, useEffect } from "react";
import { formatDate, formatNumber } from "../../utils/formatters";
import { ITEMS_PER_PAGE_OPTIONS } from "../../constants";
import SearchableSelect from "../common/SearchableSelect";

const API_BASE_URL = "https://bus-dashboard-api.phuket.cloud";

const DataTable = ({ onEdit, onDelete, refreshTrigger }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Filters
  const [filters, setFilters] = useState({
    date: "",
    bus_line: "",
  });

  // Sorting
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (filters.date) params.append("date", filters.date);
      if (filters.bus_line) params.append("bus_line", filters.bus_line);

      const response = await fetch(
        `${API_BASE_URL}/api/passenger-origins/paginated?${params}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const result = await response.json();

      if (result.success) {
        setData(result.data);
        setPagination(result.pagination);
      } else {
        throw new Error(result.error || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล");
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, filters, sortBy, sortOrder, refreshTrigger]);

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize === "all" ? 1000 : parseInt(newPageSize));
    setCurrentPage(1); // Reset to first page
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle sort column click
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // New column, default to desc
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-gray-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M5 12a1 1 0 102 0V6.414l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L5 6.414V12zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L15 13.586V8z" />
        </svg>
      );
    }
    if (sortOrder === "asc") {
      return (
        <svg
          className="w-4 h-4 ml-1 text-blue-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
            clipRule="evenodd"
          />
        </svg>
      );
    }
    return (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  const nextPage = () => {
    if (pagination.hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const goToPage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const getPageNumbers = () => {
    const pages = [];
    const totalPages = pagination.totalPages;
    const current = pagination.page;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (current <= 3) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (current >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8 mt-4">
      {/* Table Header with Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center">
            <svg
              className="w-6 h-6 mr-2 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
            ข้อมูลที่บันทึกแล้ว
            <span className="ml-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              {pagination.totalRecords} ร ายการ
            </span>
          </h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">แสดงผล:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {ITEMS_PER_PAGE_OPTIONS.filter((opt) => opt !== "all").map(
                (option) => (
                  <option key={option} value={option}>
                    {option} รายการ
                  </option>
                ),
              )}
            </select>
          </div>
        </div>

        {/* Global Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              กรองตามวันที่
            </label>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => {
                setFilters({ ...filters, date: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Bus Line Filter */}
          <div>
            <SearchableSelect
              name="bus_line"
              value={filters.bus_line}
              onChange={(e) => {
                setFilters({ ...filters, bus_line: e.target.value });
                setCurrentPage(1);
              }}
              options={[
                { value: "สนามบิน - ราไวย์", label: "สนามบิน - ราไวย์" },
                { value: "บขส.1 - ป่าตอง", label: "บขส.1 - ป่าตอง" },
                { value: "รอบเมืองเก่า", label: "รอบเมืองเก่า" },
              ]}
              placeholder="ทุกสาย"
              label="กรองตามสายรถ"
              icon={
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                    clipRule="evenodd"
                  />
                </svg>
              }
            />
          </div>

          {/* Reset Button */}
          <div>
            <button
              onClick={() => {
                setFilters({
                  date: "",
                  bus_line: "",
                });
                setSortBy("date");
                setSortOrder("desc");
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              ล้างตัวกรอง
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">
                ลำดับ
              </th>
              <th
                className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("date")}
              >
                <div className="flex items-center">
                  วันที่
                  {getSortIcon("date")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("bus_line")}
              >
                <div className="flex items-center">
                  สายรถ
                  {getSortIcon("bus_line")}
                </div>
              </th>
              <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">
                เลขรถ
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                ทะเบียนรถ
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                พนักงานขับรถ
              </th>
              <th
                className="px-6 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("tickets")}
              >
                <div className="flex items-center justify-center">
                  ตั๋ว
                  {getSortIcon("tickets")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("daypass")}
              >
                <div className="flex items-center justify-center">
                  Daypass
                  {getSortIcon("daypass")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("daypass_sold")}
              >
                <div className="flex items-center justify-center">
                  Daypass ขาย
                  {getSortIcon("daypass_sold")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("daypass_income")}
              >
                <div className="flex items-center justify-center">
                  รายได้ Daypass
                  {getSortIcon("daypass_income")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("trips")}
              >
                <div className="flex items-center justify-center">
                  เที่ยว
                  {getSortIcon("trips")}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                ป้ายรถเมล์
              </th>
              <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">
                การจัดการ
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td
                  colSpan="13"
                  className="px-6 py-12 text-center text-gray-500"
                >
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-8 w-8 text-blue-600 mr-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="text-lg">กำลังโหลดข้อมูล...</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan="13"
                  className="px-6 py-12 text-center text-red-500"
                >
                  <div>
                    <svg
                      className="w-12 h-12 mx-auto mb-4 text-red-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>เกิดข้อผิดพลาด: {error}</div>
                    <button
                      onClick={fetchData}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      ลองอีกครั้ง
                    </button>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan="13"
                  className="px-6 py-12 text-center text-gray-500"
                >
                  <div>
                    <svg
                      className="w-12 h-12 mx-auto mb-4 text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>ไม่พบข้อมูล</div>
                    <div className="text-sm">ลองปรับเกณฑ์การค้นหาใหม่</div>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 text-sm text-gray-600 text-center">
                    {(pagination.page - 1) * pagination.pageSize + index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatDate(item.date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                    {item.bus_line || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">
                    {item.bus || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-blue-600 font-medium">
                    {item.license_plate || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.driver_name || "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center font-semibold">
                    {formatNumber(item.tickets || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center font-semibold">
                    {formatNumber(item.daypass || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-purple-600 text-center font-semibold">
                    {formatNumber(item.daypass_sold || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-green-600 text-center font-semibold">
                    ฿{formatNumber(item.daypass_income || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center font-semibold">
                    {formatNumber(item.trips || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.bus_stops && item.bus_stops.length > 0 ? (
                      <div className="space-y-1">
                        {item.bus_stops.map((stop, idx) => (
                          <div key={idx} className="text-xs">
                            <span className="font-medium">
                              {stop.stop_name}
                            </span>
                            <span className="text-gray-500">
                              {" "}
                              ({stop.passengers} คน)
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onEdit && onEdit(item)}
                        className="group relative inline-flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                        title="แก้ไข"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        <span className="text-sm">แก้ไข</span>
                      </button>
                      <button
                        onClick={() => onDelete && onDelete(item)}
                        className="group relative inline-flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                        title="ลบ"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm">ลบ</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && data.length > 0 && pagination.totalPages > 1 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              แสดง {(pagination.page - 1) * pagination.pageSize + 1} ถึง{" "}
              {Math.min(
                pagination.page * pagination.pageSize,
                pagination.totalRecords,
              )}{" "}
              จาก {pagination.totalRecords} รายการ
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={prevPage}
              disabled={!pagination.hasPreviousPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                className="w-4 h-4 mr-1 inline"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              ก่อนหน้า
            </button>
            <div className="flex space-x-1">
              {getPageNumbers().map((pageNum, idx) =>
                pageNum === "..." ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="px-3 py-2 text-sm text-gray-500"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      pageNum === pagination.page
                        ? "bg-blue-600 text-white"
                        : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                ),
              )}
            </div>
            <button
              onClick={nextPage}
              disabled={!pagination.hasNextPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ถัดไป
              <svg
                className="w-4 h-4 ml-1 inline"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
